<template>
  <ul
    class="mainHeaderConnected navbar-nav ml-auto w-100"
    style="align-items: center"
  >
    <li
      v-if="$store.getters.isSandbox"
      class="mr-4"
    >
      <i class="fa fa-exclamation-triangle text-warning" /><span class="ml-2"><i>sandbox</i></span>
    </li>

    <li class="mainHeaderConnected__item nav-item show test mr-0">
      <MainHeaderNotifications v-if="displayActionReviewNotifications" />

      <NdsButton
        v-else-if="displayLegacyNotifications"
        :color="NdsButtonColor.TERTIARY"
        :size="NdsButtonSize.S"
        @click="showReview"
      >
        <NdsTooltip
          :content="title_review"
          :has-help-cursor="false"
        >
          <AppIcon
            class="mainHeaderConnected__icon"
            :color="has_reviews_ ? 'warning' : 'primary'"
            :custom_icon="has_reviews_ ? 'bell-on' : 'bell'"
            fa_type="far"
          />
        </NdsTooltip>
      </NdsButton>

      <NdsButton
        :color="NdsButtonColor.TERTIARY"
        :size="NdsButtonSize.S"
        @click="$emit('show-help')"
      >
        <NdsIcon
          :icon="NdsIconType.CIRCLE_HELP"
          :size="NdsIconSize.S"
        />
      </NdsButton>
    </li>

    <li
      v-if="!$store.getters.isPublic"
      class="show test"
    >
      <AdminWheelDropdown @refresh="(event, route) => $emit('refresh', event, route)" />
    </li>

    <li
      v-if="$store.getters.customers.length > 1"
      class="show"
    >
      <CustomerSelectorDropdown />
    </li>

    <li
      v-else-if="$store.getters.customers.length >= 0"
      class="mr-1 show"
    >
      <div class="nav-link customer">
        <span v-if="$store.getters.customer === $store.getters.none_label">
          <AppIcon
            class="icon-menu-wheel"
            color="primary"
            custom_icon="server"
            fa_type="far"
            icon_size="1rem"
          />
          <span>{{ $store.getters.instance }}</span>
        </span>
        <span v-else>
          {{ $store.getters.customer }}
        </span>
      </div>
    </li>

    <li
      class="show test"
      data-testid="toggle-user-menu"
    >
      <UserMenuDropdown />
    </li>

    <li v-if="$store.getters.impersonate_contributor">
      <i
        v-b-tooltip
        class="mr-3 ml-0 far helper text-size-main-header red fa-user-ninja"
        :title="_('Contributor mode')"
      />
    </li>
  </ul>
</template>

<script>
import { t } from '@/I18n'
import AppIcon from '@/components/ui/AppIcon.vue'
import NdsButton from '@/components/NewDesignSystem/NdsButton/NdsButton.vue'
import { NdsButtonColor, NdsButtonSize } from '@/components/NewDesignSystem/NdsButton/NdsButton.interfaces'
import NdsTooltip from '@/components/NewDesignSystem/NdsTooltip/NdsTooltip.vue'
import { NdsTooltipPosition } from '@/components/NewDesignSystem/NdsTooltip/NdsTooltip.interfaces'
import AdminWheelDropdown from '@/components/layout/main-header/connected/AdminWheelDropdown.vue'
import CustomerSelectorDropdown from '@/components/layout/main-header/connected/CustomerSelectorDropdown.vue'
import UserMenuDropdown from '@/components/layout/main-header/connected/UserMenuDropdown.vue'
import MainHeaderNotifications from '@/components/layout/main-header/connected/MainHeaderNotifications.vue'
import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'
import { NdsIconSize, NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { useActionReviewPreference } from '@/contexts/action-plans/action/Action.composables'
import { hasFeatureFlagAccess } from '@/contexts/shared/feature-flags/FeatureFlags.helpers'
import { FeatureFlag } from '@/contexts/shared/feature-flags/FeatureFlags.interfaces'

export default {
  name: 'MainHeaderConnected',
  components: {
    AppIcon,
    NdsButton,
    NdsTooltip,
    AdminWheelDropdown,
    CustomerSelectorDropdown,
    UserMenuDropdown,
    MainHeaderNotifications,
    NdsIcon,
  },
  setup() {
    const { hasActionReviewPreference, fetchActionReviewPreference } = useActionReviewPreference()

    return {
      hasActionReviewPreference,
      fetchActionReviewPreference,
    }
  },
  data() {
    return {
      NdsButtonColor,
      NdsButtonSize,
      NdsTooltipPosition,
      NdsIconType,
      NdsIconSize,
    }
  },
  computed: {
    title_review() {
      if (this.has_reviews_) {
        return t('main_header.reviews_required', this.$store.getters.nb_reviews)
      } else {
        return this._('No review required')
      }
    },
    has_reviews_() {
      return this.$store.getters.nb_reviews > 0
    },
    displayLegacyNotifications() {
      return (
        !this.hasActionReviewPreference &&
        !this.hasActionReviewFeatureFlag &&
        !this.$store.getters.isAuditor &&
        !this.$store.getters.isPublic &&
        !this.$store.getters.isOnlyContributor
      )
    },
    displayActionReviewNotifications() {
      return (
        this.hasActionReviewFeatureFlag &&
        this.hasActionReviewPreference &&
        !this.$store.getters.isAuditor &&
        !this.$store.getters.isPublic &&
        !this.$store.getters.isOnlyContributor
      )
    },
    hasActionReviewFeatureFlag() {
      return hasFeatureFlagAccess(this.$store.getters.customer_features_flags, FeatureFlag.TO_REVIEW_ACTION_STATUS)
    },
  },
  watch: {
    '$store.getters.customer_id': {
      immediate: true,
      handler(customerId) {
        if (customerId > 0) {
          this.fetchActionReviewPreference()
        }
      },
    },
  },
  methods: {
    showReview() {
      if (this.$route.name !== 'review') {
        this.$router
          .push({ name: 'review' })
          .then(() => {})
          .catch((e) => {
            this.$log.debug(e)
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mainHeaderConnected {
  &__icon {
    font-size: 20px !important;
    pointer-events: none;
  }

  &__item {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.text-size-main-header {
  font-size: 130% !important;
}

.helper {
  cursor: pointer;
}

.customer {
  font-size: 15px;
  line-height: 22px;
  font-weight: 400;
  cursor: pointer;
}

.icon-menu-wheel {
  margin-right: 8px !important;
}
</style>
